import React, { useState } from "react";
import {usePagination, useRowSelect, useTable} from "react-table";
import {IconButton} from "@material-ui/core";
import {Delete, Edit} from "@material-ui/icons";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import {Link} from "react-router-dom";
import {IndeterminateCheckbox} from "./IndeterminateCheckbox";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import PaginationBar from "../../../../common/PaginationBar";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import apiHelper from "../../../../../util/ApiHelper";
import { toast } from "react-toastify";

// Context for drag state
const IsDraggingContext = React.createContext(false);

// Reorder function for drag and drop
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

// DraggableTableCell component
class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) return null;

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) return null;

    const { width, height } = this.ref.getBoundingClientRect();
    return { width, height };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) return;

    if (snapshot) {
      if (ref.style.width === snapshot.width) return;
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) return;

    if (ref.style.width == null) return;

    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 6, paddingBottom: 6, lineHeight: "1.1em", height: "48px" }}
        ref={this.setRef}
        align={this.props.align}
        className={this.props.className}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

// DraggableTableRow component
const DraggableTableRow = ({
  snapshot,
  row,
  provided,
  orderingDisabled,
  isDragging,
}) => {
  return (
    <TableRow
      ref={provided.innerRef}
      {...provided.draggableProps}
      style={{
        ...provided.draggableProps.style,
        background: snapshot.isDragging ? "#f5f5f5" : "white",
      }}
    >
      {row.cells.map((cell, index) => {
        // Skip the dragHandle column (last column)
        return index !== row.cells.length - 1 && (
          <DraggableTableCell
            key={index}
            isDragOccurring={isDragging}
            className={cell.column.className}
          >
            {cell.render("Cell")}
          </DraggableTableCell>
        );
      })}
      
      {/* Drag handle column */}
      <TableCell
        style={{ paddingBottom: 6, paddingTop: 6, color: "#CCCCCC", cursor: "grab", lineHeight: "1.1em", height: "48px" }}
        {...(!orderingDisabled && { ...provided.dragHandleProps })}
      >
        <DragHandleIcon />
      </TableCell>
    </TableRow>
  );
};

export const EventListingTable = ({
                                    match,
                                    disabled,
                                    data,
                                    setData,
                                    categories,
                                    totalPages,
                                    totalElements,
                                    classes,
                                    singleDelete,
                                    bulkDelete,
                                    search,
                                    filterByCategory,
                                    setPage,
                                    setRowsPerPage,
                                    refreshData,
                                  }) => {
  const [keyword, setKeyword] = React.useState('')
  const [actionType, setActionType] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState("");
  
  // for drag and drop
  const [isDragging, setIsDragging] = useState(false);
  const [orderingDisabled, setOrderingDisabled] = useState(false);

  const handleSelectChange = (e) => {
    setActionType(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  }

  const triggerSearch = React.useCallback(() => {
    setOrderingDisabled(keyword ? true : false);
    search(keyword);
  }, [keyword, search]);

  const triggerFilterByCategory = React.useCallback(() => {
    // Filter by category should not disable ordering
    filterByCategory(selectedCategory);
  }, [selectedCategory, filterByCategory]);

  // Drag and drop callbacks
  const onBeforeDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result) => {
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    const items = reorder(data, result.source.index, result.destination.index);
    setData(items);
  };

  // Save ordering function
  const saveOrdering = async () => {
    try {
      const newArray = data.map((o, index) => ({
        id: o.id,
        sequence: index + pageSize * pageIndex,
      }));

      const result = await apiHelper.hkiff.updateEventSeq(newArray);
      if (result.data.code === 0) {
        toast.dark("Your changes are saved.");
        if (refreshData) {
          refreshData();
        }
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to save ordering.");
    }
  };

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link
        ref={ref} {...linkProps} />),
    []
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "localizations.en.title",
      },
      {
        Header: "Subtitle",
        accessor: "localizations.en.subtitle",
      },
      {
        Header: "Main Category",
        accessor: "category",
      },
      {
        Header: "Sub Category",
        accessor: "subCategory",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({row}) => {
          switch (row.original.status) {
            default:
            case 0:
              return <span
                className={classes.text}>Inactive</span>
            case 1:
              return <span
                className={classes.text}>Active</span>
          }
        },
      },
      {
        Header: "Last Updated",
        accessor: "updateTime",
      }
    ],
    []
  )

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    selectedFlatRows,
    state: {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      initialState: {pageIndex: 0, pageSize: 10},
      manualPagination: true,
      pageCount: totalPages,
    },
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({getToggleAllRowsSelectedProps}) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({row}) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({row}) => (
            <div
              style={{display: "inline-flex"}}>
              <IconButton
                className={classes.iconBtn}
                size="small"
                component={CustomLink}
                to={`${match.url}/${row.original.id}`}
              >
                <Edit fontSize="small"/>
              </IconButton>
              {disabled || (
                <IconButton
                  className={classes.iconBtn}
                  size="small"
                  onClick={() => {
                    singleDelete(row.original.id);
                  }}
                >
                  <Delete
                    fontSize="inherit"/>
                </IconButton>
              )}
            </div>
          ),
        },
        // Drag handle column
        {
          id: "dragHandle",
          Header: () => <></>,
        },
      ]);
    }
  );

  const handleBulkAction = React.useCallback(async () => {
    switch (actionType) {
      case "DELETE":
        const ids = selectedFlatRows.map((o) => o.original.id);
        if (ids.length > 0) {
          try {
            await bulkDelete(ids);
          } catch (e) {
            console.log(e);
          }
        }
        break;
      default:
        break;
    }
  }, [actionType, bulkDelete, selectedFlatRows]);

  React.useEffect(() => {
    setRowsPerPage(pageSize);
  }, [pageSize]);

  React.useEffect(() => {
    setPage(pageIndex);
  }, [pageIndex]);

  return (
    <>
      <Grid
        container
        justify="space-between"
        style={{padding: "0 25px 10px"}}
      >
        <Grid container item xs={4} md={4}>
          <Box width="100%"
               component="span"
               display="flex">
            <Box width="100%">
              <FormControl fullWidth
                           margin="dense"
                           variant="outlined">
                <InputLabel
                  id="demo-simple-select-outlined-label">
                  Bulk Actions
                </InputLabel>
                <Select
                  labelId="demo-simple-select-outlined-label"
                  id="demo-simple-select-outlined"
                  value={actionType}
                  onChange={handleSelectChange}
                  label="Bulk Actions"
                >
                  <MenuItem
                    value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem
                    value={"DELETE"}>Delete</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBulkAction}
                disabled={disabled}
              >
                Apply
              </Button>
            </Box>
            <Box width="100%">
              <FormControl fullWidth
                           margin="dense"
                           variant="outlined">
                <InputLabel
                  id="demo-simple-select-outlined-label">
                  Select Category
                </InputLabel>
                <Select
                  labelId="demo-simple-select-outlined-label"
                  id="demo-simple-select-outlined"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Select Category"
                >
                  <MenuItem
                    value={null}>
                    <em>All Category</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem
                        key={category.id}
                        value={category.id}>
                        {category.nameEn}
                    </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                onClick={triggerFilterByCategory}
              >
                Filter
              </Button>
            </Box>
            <Box display="flex" style={{minWidth: "140px"}}>
              <Button
                variant="contained"
                color="primary"
                onClick={saveOrdering}
                disabled={orderingDisabled || disabled}
                style={{whiteSpace: "nowrap"}}
              >
                Save Changes
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid container item xs={12} md={8}>
          <Box
            width="100%"
            component="span"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                type="button"
                component={Link}
                disabled={disabled}
                to={`${match.url}/new`}
              >
                Add New Event
              </Button>
            </Box>
            <Box display="flex">
              <TextField
                id="outlined-error"
                label="Search"
                variant="outlined"
                margin="dense"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                type="button"
                margin="dense"
                onClick={triggerSearch}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container xs={12}>
        <TableContainer
          component={Paper}
          style={{
            margin: "0 25px",
            boxShadow: "none",
            border: "1px solid #cccccc",
          }}
        >
          <fieldset disabled={disabled}
                    className="fieldset">
            <Table
              aria-label="simple table"
              component="ul"
              style={{margin: "0"}}
              {...getTableProps()}
            >
              <TableHead
                component="div">
                {headerGroups.map((headerGroup) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <TableCell
                        {...column.getHeaderProps()}
                        className={`${column.className ?? ""}`}
                        style={{
                          paddingTop: 6,
                          paddingBottom: 6,
                          lineHeight: "1.1em",
                          height: "48px",
                        }}
                      >
                        {column.render("Header")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <DragDropContext
                onBeforeDragStart={onBeforeDragStart}
                onDragEnd={onDragEnd}
              >
                <Droppable droppableId="event-table-droppable">
                  {(provided, snapshot) => (
                    <TableBody
                      component="div"
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      <IsDraggingContext.Provider value={isDragging}>
                        {rows.map((row, index) => {
                          prepareRow(row);
                          return (
                            <Draggable
                              key={row.original.id}
                              draggableId={String(row.original.id)}
                              index={index}
                              isDragDisabled={disabled || orderingDisabled}
                            >
                              {(provided, snapshot) => (
                                <DraggableTableRow
                                  key={row.original.id}
                                  row={row}
                                  provided={provided}
                                  snapshot={snapshot}
                                  orderingDisabled={orderingDisabled}
                                  isDragging={isDragging}
                                />
                              )}
                            </Draggable>
                          );
                        })}
                      </IsDraggingContext.Provider>
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </fieldset>
        </TableContainer>
      </Grid>
      <Grid
        container
        justify="flex-end"
        style={{
          paddingRight: "25px",
          marginTop: "15px"
        }}
      >
        <div className={classes.flexRow}>
          <div></div>
          <PaginationBar
            pageSize={pageSize}
            setPageSize={setPageSize}
            gotoPage={gotoPage}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageIndex={pageIndex}
            totalCount={totalElements}
          />
        </div>
      </Grid>
    </>
  )
}
